// Keepers — this year's kept players, pre-assigned to a seat at a price.
//
// v1 COST MODEL: explicit price only. `{name, pos, price}`. No round-based
// cost, no annual escalation — those are different data models and promising
// them here would bake in the wrong one (see builder-prompts-keepers-aug9.md).
//
// WHERE A KEEPER LIVES. On the per-seat config blob, under the seat's config
// KEY, as `keepers`. That blob is already persisted per league
// (useMockDraft.js `cfgKey`), so keepers are per-league for free.
//
//   mockTeamConfigs["Mike"].keepers  = [{ name: "Bijan Robinson", pos: "RB", price: 42 }]
//   mockTeamConfigs.__user.keepers   = [...]        // the user's own seat
//
// The `__user` asymmetry is not ours — it mirrors buildDraftTeams, which reads
// `teamConfigs.__user` for the user's seat and `teamConfigs[ownerKey]` for
// every other one (simulation.js:216). Keeping the same rule here is what stops
// the two from drifting. It also comes with a free win: `sanitizeTeamConfigs`
// carries `__`-prefixed keys through verbatim, so a user-seat keeper needs no
// whitelist entry at all.
//
// NO PLAYER OBJECT IS STORED. Only name + pos + price. The pool changes between
// sessions, and a stale embedded player object with a stale `value` would poison
// heroRBDrafted, the tier anchors, and the results screen. Resolution happens at
// draft start, every time.
//
// NAME IS THE JOIN KEY, everywhere. The drafted-set is a Set of `p.name`
// (useMockDraft.js `mockDraftedPlayers`, simulation.js `draftedPlayers`), so
// name is what identifies a player to the pool-exclusion path and it must not
// drift into `${name}-${pos}` here.

import { UNIQUE_PLAYERS } from "../data/players";
import { MOCK_ROSTER_SIZE } from "../data/archetypes";
import { buildRosterSlots } from "./rosterSlotFill";

// The user's seat reads this reserved config key, exactly as buildDraftTeams
// does. Not a TEAM_NAME, so it can never collide with an opponent seat.
export const USER_CONFIG_KEY = "__user";

// Roster size for a settings object, derived the same way useMockDraft derives
// it (useMockDraft.js:272) so the two can't disagree about how many slots a
// keeper is competing with.
export function keeperRosterSize(settings) {
  const n = settings && settings.positions ? buildRosterSlots(settings.positions).length : 0;
  return n || MOCK_ROSTER_SIZE;
}

// ── Shape validation, for the persistence boundary ───────────────────
// `sanitizeTeamConfigs` is a strict whitelist: it rebuilds each seat from a
// fixed key list, so anything it does not name is silently gone after a reload.
// `keepers` is now on that list, and this is the defensive load it gets —
// the same posture `modifiers` already uses.
//
// Deliberately silent. A malformed record is dropped, not repaired and not
// reported: this runs on every config load, long before there is a UI to show
// a complaint to. Real user-facing problems are `validateKeepers`' job, and it
// runs on the sanitized data.
export function sanitizeKeepers(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const k of raw) {
    if (!k || typeof k !== "object") continue;
    const name = typeof k.name === "string" ? k.name.trim() : "";
    const pos = typeof k.pos === "string" ? k.pos.trim() : "";
    if (!name || !pos) continue;
    // Integers only, floored at $1. A $0 or fractional keeper would put a
    // non-integer into `team.budget` and every dollar figure downstream.
    const price = Math.max(1, Math.floor(Number(k.price)));
    if (!Number.isFinite(price)) continue;
    // Within one seat, the same name twice is meaningless — the second copy
    // would occupy a roster slot and dock a budget for a player already kept.
    if (seen.has(name)) continue;
    seen.add(name);
    out.push({ name, pos, price });
  }
  return out;
}

// Every config key that carries at least one keeper, sanitized. Includes keys
// that may not be seated in this league — resolution reports them rather than
// dropping them, because only buildDraftTeams knows which keys get a seat (the
// opponent pool is shuffled, and the canonical room drops the last name).
function keeperEntries(teamConfigs) {
  const out = [];
  if (!teamConfigs || typeof teamConfigs !== "object") return out;
  for (const key of Object.keys(teamConfigs)) {
    const list = sanitizeKeepers(teamConfigs[key]?.keepers);
    if (list.length) out.push([key, list]);
  }
  return out;
}

// Does this config blob carry any keeper at all? A key scan, deliberately
// cheaper than a resolution pass, so the zero-keeper path — which is every
// draft in a non-keeper league — never enters the keeper machinery at all.
// That is addendum B's invariant made structural rather than argued: code that
// is never entered cannot consume an RNG draw or perturb an iteration order.
//
// Deliberately does NOT sanitize, so it errs toward entering the machinery: a
// blob holding nothing but malformed rows answers true here and then resolves to
// nothing, which is wasteful and harmless. The dangerous direction — answering
// false while a real keeper exists — is impossible.
export function hasKeepers(teamConfigs) {
  if (!teamConfigs || typeof teamConfigs !== "object") return false;
  for (const key of Object.keys(teamConfigs)) {
    const raw = teamConfigs[key]?.keepers;
    if (Array.isArray(raw) && raw.length) return true;
  }
  return false;
}

// Every config key with a keepers array, UNSANITIZED. The UI needs the raw rows:
// a half-typed row is exactly what the user is looking at, and the shape filter
// would throw it away before it could be complained about.
function rawKeeperEntries(teamConfigs) {
  const out = [];
  if (!teamConfigs || typeof teamConfigs !== "object") return out;
  for (const key of Object.keys(teamConfigs)) {
    const raw = teamConfigs[key]?.keepers;
    if (Array.isArray(raw) && raw.length) out.push([key, raw]);
  }
  return out;
}

// Name -> player, built once. First occurrence wins, which is UNIQUE_PLAYERS'
// own rule. Lazily built so importing this module costs nothing.
let _byName = null;
function playerByName() {
  if (!_byName) {
    _byName = new Map();
    for (const p of UNIQUE_PLAYERS) if (!_byName.has(p.name)) _byName.set(p.name, p);
  }
  return _byName;
}

// ── Resolution ───────────────────────────────────────────────────────
// Map each stored `{name, pos, price}` to the live player object, by exact
// `name` match against UNIQUE_PLAYERS.
//
// Returns { byOwner, unresolved, warnings }:
//   byOwner    — { [configKey]: [{ player, price }] }, ready for applyKeepers
//   unresolved — [{ owner, name, pos, price }] names not in this year's pool
//   warnings   — plain-English strings worth surfacing but not worth blocking
//
// byOwner is keyed by CONFIG KEY, not by seat index, and that is load-bearing:
// buildDraftTeams shuffles the opponent pool, so a given config key lands on a
// different slot every draft. The seat is derived from the key inside
// applyKeepers, which is the only place that knows the mapping.
//
// The plan sketched `(teamConfigs, teamCount, settings)`. teamCount is dropped
// because nothing here needs it — the seat count is `settings.teams` when it is
// ever wanted, and which keys get seated is a question only buildDraftTeams can
// answer (applyKeepers reports the leftovers).
export function resolveKeepers(teamConfigs, settings) {
  const byOwner = {};
  const unresolved = [];
  const warnings = [];
  const byName = playerByName();

  for (const [owner, list] of keeperEntries(teamConfigs)) {
    for (const k of list) {
      const player = byName.get(k.name);
      if (!player) {
        // Retired, renamed, or simply not on this year's board. Drop it and say
        // so. NEVER fabricate a player object: a synthetic entry has no `value`,
        // and `value` is what heroRBDrafted, the tier anchors and the results
        // screen all read.
        unresolved.push({ owner, name: k.name, pos: k.pos, price: k.price });
        continue;
      }
      if (player.pos !== k.pos) {
        // Trust the resolved player. The stored pos is a convenience copy for
        // the UI; the pool is the truth about what a player plays.
        warnings.push(
          `${k.name} is listed as ${k.pos} but is a ${player.pos} on this year's board — kept as ${player.pos}.`
        );
      }
      (byOwner[owner] ||= []).push({ player, price: k.price });
    }
  }

  return { byOwner, unresolved, warnings };
}

// ── Where the keeper section lives ───────────────────────────────────
// True when the Keepers section should be PROMOTED from its home inside
// "Customize Opponents, Keepers & More" up to the main setup page.
//
// Three triggers, any one sufficient:
//   1. the league already has a keeper entered — they are using the feature;
//   2. the import detected keeper history, via leagueMarket's existing
//      `keeperShare` (> 5% of priced picks were keepers). That one promotes even
//      at ZERO entered keepers, on purpose: an imported keeper league is exactly
//      the user who came here for this.
//   3. the user SAID SO — `isKeeperLeague` on the league record, answered at
//      creation on the essentials step. The most direct signal of the three and
//      the only one that works for a hand-built league with nothing imported and
//      nothing entered yet: someone who ticks "keeper league? yes" should find
//      the section waiting for them, not have to go looking for it inside a
//      collapsed panel to enter their first keeper.
//
// The 0.05 floor and the signal itself are leagueMarket's, reused rather than
// re-derived — it is the same number the market card prints "keeper league — N
// kept players excluded" from, so the two cannot disagree about whether this is a
// keeper league.
//
// Lives here rather than inline in MockSetup so the rule is unit-testable and
// mutation-testable on its own, instead of only observable through a rendered
// tree.
export const KEEPER_SHARE_PROMOTE_FLOOR = 0.05;

export function shouldPromoteKeeperSection(teamConfigs, marketProfile, league) {
  if (hasKeepers(teamConfigs)) return true;
  // Strict === true: the flag is absent on every league saved before it
  // existed, and a truthy-coerced undefined would be a silent behaviour change
  // for all of them.
  if (league?.isKeeperLeague === true) return true;
  const share = marketProfile?.keeperShare;
  return typeof share === "number" && share > KEEPER_SHARE_PROMOTE_FLOOR;
}

// ── The seat list the keeper picker offers ───────────────────────────
// [{ key, label, isUser }], the user's own seat FIRST.
//
// `opponentKeys` must be the list buildDraftTeams will actually seat — pass
// `opponentSeats(teamCount, userOwner)` from engine/simulation, which is the one
// derivation buildDraftTeams itself calls, canonical-room rule included.
// Offering any other set is how a keeper lands on a config key that never gets a
// seat and vanishes with no error.
//
// THE "(CPU)" SUFFIX, and why it is not cosmetic. Under the canonical room a user
// who names themselves "Mike" sits next to the AI seat whose config key is also
// "Mike", and simulation.js relabels that AI seat "Mike (CPU)" so the two are
// distinguishable. Their keepers are NOT interchangeable: the user's live under
// "__user" and the AI's under "Mike". Two identically-labelled options in this
// picker would let someone hand their own keeper to the CPU and never know, so
// the suffix is reproduced here from the same condition.
export function keeperSeatOptions(teamConfigs, opponentKeys, userOwner, canonicalRoom) {
  const self = String(userOwner || "").trim();
  const opts = [{ key: USER_CONFIG_KEY, label: self ? `Your team (${self})` : "Your team", isUser: true }];
  for (const key of opponentKeys || []) {
    const shown = teamConfigs?.[key]?.customName?.trim() || key;
    const collides = canonicalRoom && self && shown.toLowerCase() === self.toLowerCase();
    opts.push({ key, label: collides ? `${shown} (CPU)` : shown, isUser: false });
  }
  return opts;
}

// Typeahead over the live pool. Exported so the keeper UI searches exactly the
// set resolution will later join against — a picker fed from anywhere else could
// offer a name that then fails to resolve.
export function searchKeeperPlayers(query, limit = 8) {
  const q = (query || "").trim().toLowerCase();
  if (q.length < 2) return [];
  const starts = [], contains = [];
  for (const p of UNIQUE_PLAYERS) {
    const n = p.name.toLowerCase();
    if (n.startsWith(q)) starts.push(p);
    else if (n.includes(q)) contains.push(p);
    if (starts.length >= limit) break;
  }
  return [...starts, ...contains].slice(0, limit);
}

// Exact-name lookup against the same map resolution uses. Returns null for a
// name that is not on this year's board.
export function findKeeperPlayer(name) {
  return playerByName().get((name || "").trim()) || null;
}

// Total keeper dollars per config key, from the sanitized records. Exported so
// the UI's per-seat readout and the budget check below cannot disagree about
// the arithmetic.
export function keeperSpendByOwner(teamConfigs) {
  const out = {};
  for (const [owner, list] of keeperEntries(teamConfigs)) {
    out[owner] = list.reduce((s, k) => s + k.price, 0);
  }
  return out;
}

// ── Validation — the four ways a user breaks this ─────────────────────
// Returns a list of plain-English problems. Empty list = safe to draft.
// Chunk 3 renders these and disables START DRAFT while any exist; a keeper
// league that silently drops a keeper is worse than one that refuses to start.
//
// These are REJECTIONS, not clamps. Silently clamping a keeper price is the
// failure mode that looks like the feature working.
export function validateKeepers(teamConfigs, settings) {
  const problems = [];
  const entries = keeperEntries(teamConfigs);
  const raw = rawKeeperEntries(teamConfigs);
  if (!entries.length && !raw.length) return problems;

  const rosterSize = keeperRosterSize(settings);
  const budget = settings?.budget ?? 200;

  // 0. A name that isn't on this year's board. BLOCKING, not a warning: starting
  //    anyway would drop the keeper, and the user would draft against a roster
  //    that isn't there — the one failure they cannot see for themselves. Names
  //    go stale between seasons (retirements, spelling), so this fires on real
  //    saved leagues and must name the player so it is one edit to fix.
  //
  //    Read from the RAW rows, not the sanitized ones: a half-typed row has no
  //    `pos` yet and the shape filter drops it, so it would otherwise only ever
  //    surface as the generic "incomplete" line below.
  for (const [owner, list] of raw) {
    for (const k of list) {
      const nm = typeof k?.name === "string" ? k.name.trim() : "";
      if (!nm) continue;                      // covered by the incomplete-row line
      if (playerByName().has(nm)) continue;
      problems.push(`"${nm}" (${label(teamConfigs, owner)}) isn't on this year's board — check the spelling or pick from the list.`);
    }
  }

  // 3. Same player kept by two seats. Checked first because it is the one that
  //    fails silently: the drafted-set is keyed by name, so the second seat's
  //    copy is swallowed with no error anywhere.
  const claimedBy = new Map();
  for (const [owner, list] of entries) {
    for (const k of list) {
      const prior = claimedBy.get(k.name);
      if (prior && prior !== owner) {
        problems.push(`${k.name} is kept by two seats (${label(teamConfigs, prior)} and ${label(teamConfigs, owner)}). A player can only be kept once.`);
      } else if (!prior) {
        claimedBy.set(k.name, owner);
      }
    }
  }

  for (const [owner, list] of entries) {
    const who = label(teamConfigs, owner);
    const spend = list.reduce((s, k) => s + k.price, 0);

    // 2. More keepers than roster slots.
    if (list.length >= rosterSize) {
      problems.push(`${who} has ${list.length} keepers but only ${rosterSize} roster slots. Keep at most ${rosterSize - 1}.`);
      continue;                       // the budget math below is meaningless now
    }

    // 1. Keeper spend leaves the team unable to fill its remaining slots at
    //    $1 apiece. This is the one that hangs a draft rather than looking wrong.
    const remainingSlots = rosterSize - list.length;
    const maxSpend = budget - remainingSlots;
    if (spend > maxSpend) {
      // "leaving $-50" is how a negative reads if you just interpolate it. Say
      // what actually happened. Same fix as the per-seat readout in KeeperEntry.
      const left = budget - spend;
      const leftPhrase = left < 0 ? `$${Math.abs(left)} OVER budget` : `only $${left}`;
      problems.push(
        `${who} keeps ${list.length} player${list.length === 1 ? "" : "s"} for $${spend}, ` +
        `${leftPhrase} with ${remainingSlots} slot${remainingSlots === 1 ? "" : "s"} still to fill. ` +
        `The most that seat can spend on keepers is $${maxSpend}.`
      );
    }
  }

  // 4. Price problems are absorbed by sanitizeKeepers (floored at $1, integers
  //    only), so by the time a record reaches here it cannot be non-positive.
  //    What IS still worth saying is when a record was thrown away entirely.
  const dropped = countDropped(teamConfigs);
  if (dropped) {
    problems.push(`${dropped} keeper row${dropped === 1 ? " is" : "s are"} incomplete — every keeper needs a player and a price.`);
  }

  return problems;
}

// How the seat is named to the user: their own label if they typed one, the
// config key otherwise. The stored key stays a TEAM_NAME either way — renaming
// keys orphans every config and drops the room to Balanced (useMockDraft.js:71-75).
function label(teamConfigs, owner) {
  if (owner === USER_CONFIG_KEY) return "Your team";
  return teamConfigs?.[owner]?.customName?.trim() || owner;
}

// Rows the shape filter threw away AND that nothing else already named. Counted
// rather than described because the UI knows which row is which and can point at
// it; this only has to make the count non-zero so START DRAFT blocks.
//
// Rows carrying a non-empty but unresolvable name are excluded: the check above
// already reports those BY NAME, and counting them here too gave one half-typed
// row two complaints ("bij" isn't on the board, AND 1 row is incomplete). One
// problem per problem.
function countDropped(teamConfigs) {
  let n = 0;
  if (!teamConfigs || typeof teamConfigs !== "object") return 0;
  const byName = playerByName();
  for (const key of Object.keys(teamConfigs)) {
    const raw = teamConfigs[key]?.keepers;
    if (!Array.isArray(raw)) continue;
    const named = raw.filter(k => {
      const nm = typeof k?.name === "string" ? k.name.trim() : "";
      return nm && !byName.has(nm);
    }).length;
    n += raw.length - sanitizeKeepers(raw).length - named;
  }
  return Math.max(0, n);
}
